import React from "react";
import { useSelector } from "react-redux";
import useConversation from "./useConversation";
import MessageListColumn from "./MessageListColumn";
import ThreadColumn from "./ThreadColumn";
import TasksColumn from "./TasksColumn";
import PlatformExecuteModal from "./PlatformExecuteModal";
import { ConversationProvider } from "./ConversationContext";
import { formatIntegrationName } from "../../constants/constants";
import { NO_NEW_MESSAGE_PLATFORMS } from "./Conversation.utils";

function ConversationMessages({
  group,
  isLoading,
  integrationSettingId,
  taskUuid,
}) {
  const { meta, listData, threadData, tasksData, modal } = useConversation({
    group,
    isLoading,
    integrationSettingId,
    taskUuid,
  });

  const { members = [] } = useSelector((state) => state.teamMember);

  if (!group) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-body-secondary">
        <i className="bi bi-chat-dots fs-1 mb-3" />
        <h6>Select a conversation</h6>
        <small>Choose a channel from the sidebar to view messages</small>
      </div>
    );
  }

  const canSendToChannel = !NO_NEW_MESSAGE_PLATFORMS.includes(
    String(group.platform).toLowerCase(),
  );
  const displayedMessageCount =
    group.messageCount ?? group.messages?.length ?? 0;

  return (
    <ConversationProvider value={{ meta, authorMap: threadData.authorMap }}>
      <div className="d-flex flex-column flex-grow-1 overflow-hidden text-body">
        <div className="flex-shrink-0 border-bottom border-secondary-subtle">
          <div className="d-flex align-items-center justify-content-between px-3 pt-3 pb-3">
            <div className="min-w-0">
              <h4 className="fw-bold mb-0 text-body text-truncate">
                {formatIntegrationName(group.label)}
              </h4>
              <small className="text-body-secondary text-truncate d-block">
                {formatIntegrationName(group.description || group.platform)}
              </small>
            </div>

            <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-3">
              <span className="badge bg-primary-subtle text-primary-emphasis rounded-pill px-3 py-2 d-flex align-items-center conv-msgcount-badge">
                <i className="bi bi-chat-left-text me-1" />
                {displayedMessageCount} messages
              </span>

              {canSendToChannel && (
                <button
                  type="button"
                  className={`btn btn-sm rounded-pill px-3 d-flex align-items-center gap-1 conv-newmsg-btn ${
                    threadData.isComposerOpen
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={threadData.toggleComposer}
                >
                  <i className="bi bi-pencil-square" />
                  New Message
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="d-flex"
          style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}
        >
          <MessageListColumn listData={listData} />
          <ThreadColumn threadData={threadData} />
          <TasksColumn tasksData={tasksData} members={members} />
        </div>

        <PlatformExecuteModal
          modalState={modal.modalState}
          data={modal.data}
          handlers={modal.handlers}
          isExecuting={modal.isExecuting}
        />
      </div>
    </ConversationProvider>
  );
}

export default ConversationMessages;
